/**
 * The hardware-readiness checklist as a pure function (CAM-01, GDS §12).
 *
 * Readings in, checklist out. Nothing here touches a stream, a model or the DOM, so the states
 * that matter most — a dark room, a child too close, a head cut off by a camera pointed at the
 * floor — are ordinary test cases rather than things a fake webcam has to be persuaded to produce.
 *
 * What arrives here is deliberately **numbers only**: no landmarks, no frames. The camera session
 * derives these readings inside the pose boundary and hands across measurements, so the shell can
 * render a readiness screen without ever being able to see a child's body (architecture §11, §25).
 */

import {
  CAMERA_READINESS_THRESHOLDS,
  COOPERATIVE_READINESS_CHECK_LABEL,
  COOPERATIVE_READINESS_MESSAGE,
  INVENTED_COOPERATIVE_READINESS_COPY,
  INVENTED_READINESS_COPY,
  READINESS_CHECK_LABELS,
  READINESS_MESSAGES,
  type CameraReadinessThresholds,
} from '../../platform/config/camera-readiness';
import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { CameraMode } from '../../shared/types/camera-mode';

/** Overrides for the two config-layer inputs, so a test can pin either without a global change. */
export interface ReadinessOptions {
  readonly thresholds?: CameraReadinessThresholds;
  /** Defaults to the flagged `pose.lowPerformanceFrameMs` placeholder (D-060(5)). */
  readonly lowPerformanceFrameMs?: number;
  /**
   * Whether this sitting has two heroes in it (Slice A′, CAM-07).
   *
   * Off by default, and everything cooperative hangs off it: the ninth GDS §12 row, its message, and
   * D-076(4)'s Full-Motion-only rule. A single-player family's screen is byte-identical to what it
   * was before this existed — a solo child is never told about a width they do not need.
   */
  readonly cooperative?: boolean;
}

/** Which of GDS §12's checks this runs. `cooperativeWidth` appears only in a two-hero sitting. */
export type ReadinessCheckId =
  | 'browserSupport'
  | 'cameraAvailable'
  | 'permissionGranted'
  | 'poseSystemLoaded'
  | 'lighting'
  | 'playerDistance'
  | 'bodyVisibility'
  | 'cooperativeWidth'
  | 'performance';

/**
 * `pending` — not measured yet · `ready` — good · `attention` — the child or the room can fix it ·
 * `blocked` — nothing the child can do from here (no camera, refused permission, dead model).
 */
export type ReadinessCheckStatus = 'pending' | 'ready' | 'attention' | 'blocked';

export interface ReadinessCheck {
  readonly id: ReadinessCheckId;
  /** GDS §12's own wording for the check. */
  readonly label: string;
  readonly status: ReadinessCheckStatus;
  /** What the screen should say about this check, or null when there is nothing to say. */
  readonly message: string | null;
}

/**
 * GDS §6's two distance bands. The parent chooses; readiness only recommends (D-059).
 *
 * Re-exported rather than declared here since Slice F: the type is now also read by the parent
 * setup screen and by `SessionLaunchConfig`, so it lives in `src/shared/types/camera-mode.ts`.
 * Every existing import of it from this module keeps working.
 */
export type { CameraMode };

export interface ReadinessReport {
  readonly checks: readonly ReadinessCheck[];
  /** The one child-facing line the screen leads with. */
  readonly headline: string;
  /** The mode the measured distance suggests, or null while that is unknown. */
  readonly recommendedMode: CameraMode | null;
  /** Every check passed. */
  readonly ready: boolean;
  /** Something is wrong that the child cannot fix by moving. */
  readonly blocked: boolean;
}

/** Whether the browser will give us a camera at all. */
export type CameraPermissionState = 'unknown' | 'granted' | 'denied' | 'unavailable';

/** How far the pose model has got. Mirrors the engine's own lifecycle. */
export type PoseEngineReadiness = 'idle' | 'loading' | 'ready' | 'failed';

/** Measurements taken from the body the pose engine found. Derived inside the pose boundary. */
export interface BodyReadings {
  /** Vertical span of the visible core landmarks, as a fraction of frame height. */
  readonly heightFraction: number;
  /** Fraction of the core landmarks that are confidently visible. */
  readonly visibleCoreFraction: number;
  /** Clear frame above the highest visible landmark, as a fraction of frame height. */
  readonly topEdgeGap: number;
  /** The pose's derived core confidence (the D-059 PS3-1 value). */
  readonly coreConfidence: number;
}

/**
 * What the camera can tell about **two** bodies at once (GDS §12 "Cooperative frame width").
 *
 * Settled rather than instantaneous: the gate that produces it absorbs the 5–7 frame second-body
 * acquisition the Slice 0′ spike measured (D-077(2)(d)), so a family standing still is never shown a
 * row changing its mind. Null until something has held long enough to be worth saying.
 *
 * Produced only in a cooperative sitting, and only when the engine has been asked for two bodies —
 * a one-pose engine cannot honestly report on a second child, so it reports nothing.
 */
export interface CooperativeReadings {
  /** Two distinct bodies are in the picture. */
  readonly twoBodies: boolean;
  /** Whether the gap between them fits two lanes. Null while there is only one body. */
  readonly enoughWidth: boolean | null;
  /** The measured gap between their centres, as a fraction of frame width. */
  readonly centreSeparation: number | null;
}

export interface CameraReadings {
  readonly browserSupported: boolean;
  /** Null until the device list has been read. */
  readonly cameraAvailable: boolean | null;
  readonly permission: CameraPermissionState;
  readonly poseEngine: PoseEngineReadiness;
  /** Mean luminance of a sampled frame, 0–1. Null until a frame has been sampled. */
  readonly meanLuminance: number | null;
  /** Null when no body is in frame — which is a state, not a failure. */
  readonly body: BodyReadings | null;
  /**
   * The two-hero measurement, or null. Null in every single-player sitting, where nothing is
   * looking for a second body and pretending otherwise would be a guess.
   */
  readonly cooperative: CooperativeReadings | null;
  /** Recent mean detection time in ms. Null until detection has run. */
  readonly frameTimeMs: number | null;
}

const PENDING = INVENTED_READINESS_COPY.checking;

/**
 * The row labels, in GDS §12's own words.
 *
 * Two records joined rather than one: `READINESS_CHECK_LABELS` is deliberately still exactly the
 * single-player checklist (D-061(2)), so the cooperative row's label lives beside it and is added
 * here, where a checklist is actually being built.
 */
const CHECK_LABELS: Readonly<Record<ReadinessCheckId, string>> = {
  ...READINESS_CHECK_LABELS,
  cooperativeWidth: COOPERATIVE_READINESS_CHECK_LABEL,
};

function check(
  id: ReadinessCheckId,
  status: ReadinessCheckStatus,
  message: string | null,
): ReadinessCheck {
  return { id, label: CHECK_LABELS[id], status, message };
}

function distanceCheck(
  readings: CameraReadings,
  thresholds: CameraReadinessThresholds,
  cooperative: boolean,
): { check: ReadinessCheck; mode: CameraMode | null } {
  const { body } = readings;
  const { distance } = thresholds;

  if (body === null) {
    // Unknowable without a body. Pending, not failing: the body-visibility row is the one that
    // asks the child to step into the picture, and two rows shouting the same thing is noise.
    return { check: check('playerDistance', 'pending', PENDING), mode: null };
  }

  if (body.heightFraction >= distance.tooCloseBodyHeight) {
    return {
      check: check('playerDistance', 'attention', READINESS_MESSAGES.moveBack),
      mode: null,
    };
  }
  if (body.heightFraction >= distance.compactMinBodyHeight) {
    /**
     * The Compact band, which two heroes cannot use (D-076(4)).
     *
     * GDS §6 and MIN §6 pair cooperative play exclusively with Full-Motion's wider distance band,
     * and the readiness system's own cooperative message is a width floor. So a coop sitting reads
     * the Compact band as a room to step back in — in GDS §12's own words — rather than as a mode
     * on offer. A single-player sitting is untouched: Compact is its accessibility path (GDS §15).
     */
    if (cooperative) {
      return {
        check: check('playerDistance', 'attention', READINESS_MESSAGES.moveBack),
        mode: null,
      };
    }
    return {
      check: check('playerDistance', 'ready', READINESS_MESSAGES.compactReady),
      mode: 'compact',
    };
  }
  if (body.heightFraction >= distance.fullMotionMinBodyHeight) {
    return {
      check: check('playerDistance', 'ready', READINESS_MESSAGES.fullMotionReady),
      mode: 'full-motion',
    };
  }
  if (body.heightFraction >= distance.tooFarBodyHeight) {
    return {
      check: check('playerDistance', 'attention', INVENTED_READINESS_COPY.comeCloser),
      mode: null,
    };
  }
  // So far away that the landmarks are not worth thresholding — a room-scale problem, not a step.
  return {
    check: check('playerDistance', 'blocked', INVENTED_READINESS_COPY.comeCloser),
    mode: null,
  };
}

function visibilityCheck(
  readings: CameraReadings,
  thresholds: CameraReadinessThresholds,
): ReadinessCheck {
  const { body } = readings;
  if (readings.poseEngine !== 'ready') return check('bodyVisibility', 'pending', PENDING);
  if (body === null) {
    return check('bodyVisibility', 'attention', INVENTED_READINESS_COPY.stepIntoView);
  }
  if (body.topEdgeGap < thresholds.visibility.headroomFraction) {
    // The body reaches the top edge: the camera is aimed too low and the head is being cut off.
    return check('bodyVisibility', 'attention', READINESS_MESSAGES.cameraTooLow);
  }
  if (body.visibleCoreFraction < thresholds.visibility.minVisibleCoreFraction) {
    return check('bodyVisibility', 'attention', INVENTED_READINESS_COPY.stepIntoView);
  }
  return check('bodyVisibility', 'ready', null);
}

/**
 * GDS §12's ninth row — the one D-061(2) held back for this phase (CAM-07).
 *
 * It answers one question and refuses the others: are two children in the picture, far enough apart
 * to have a lane each? *Whose* body is whose is Slice B′'s problem and is not asked here, so nothing
 * on this screen can attribute anything to a child (D-076(1)).
 */
function cooperativeWidthCheck(readings: CameraReadings): ReadinessCheck {
  const pair = readings.cooperative;
  if (readings.poseEngine !== 'ready' || pair === null) {
    return check('cooperativeWidth', 'pending', PENDING);
  }
  if (!pair.twoBodies) {
    return check(
      'cooperativeWidth',
      'attention',
      INVENTED_COOPERATIVE_READINESS_COPY.bothHeroesNeeded,
    );
  }
  if (pair.enoughWidth !== true) {
    return check('cooperativeWidth', 'attention', COOPERATIVE_READINESS_MESSAGE);
  }
  return check('cooperativeWidth', 'ready', null);
}

/**
 * Runs GDS §12's readiness checks over one set of readings.
 *
 * The headline is the first thing that needs saying, in the spec's own check order — the doc gives
 * no priority between, say, a dark room and a child standing too close, so the order it lists the
 * checks in is the order used rather than an invented ranking.
 */
export function evaluateReadiness(
  readings: CameraReadings,
  options: ReadinessOptions = {},
): ReadinessReport {
  const thresholds = options.thresholds ?? CAMERA_READINESS_THRESHOLDS;
  const lowPerformanceFrameMs =
    options.lowPerformanceFrameMs ?? DEFAULT_GAMEPLAY_TUNABLES.pose.lowPerformanceFrameMs;

  const cooperative = options.cooperative === true;
  const distance = distanceCheck(readings, thresholds, cooperative);

  const checks: ReadinessCheck[] = [
    readings.browserSupported
      ? check('browserSupport', 'ready', null)
      : check('browserSupport', 'blocked', INVENTED_READINESS_COPY.unsupportedBrowserTitle),

    readings.cameraAvailable === null
      ? check('cameraAvailable', 'pending', PENDING)
      : readings.cameraAvailable
        ? check('cameraAvailable', 'ready', null)
        : check('cameraAvailable', 'blocked', INVENTED_READINESS_COPY.noCameraTitle),

    readings.permission === 'granted'
      ? check('permissionGranted', 'ready', null)
      : readings.permission === 'denied'
        ? check('permissionGranted', 'blocked', INVENTED_READINESS_COPY.permissionDeniedTitle)
        : readings.permission === 'unavailable'
          ? check('permissionGranted', 'blocked', INVENTED_READINESS_COPY.noCameraTitle)
          : check('permissionGranted', 'pending', PENDING),

    readings.poseEngine === 'ready'
      ? check('poseSystemLoaded', 'ready', null)
      : readings.poseEngine === 'failed'
        ? check('poseSystemLoaded', 'blocked', INVENTED_READINESS_COPY.poseUnavailableTitle)
        : check('poseSystemLoaded', 'pending', PENDING),

    readings.meanLuminance === null
      ? check('lighting', 'pending', PENDING)
      : readings.meanLuminance < thresholds.lighting.minMeanLuminance
        ? check('lighting', 'attention', READINESS_MESSAGES.moreLight)
        : check('lighting', 'ready', null),

    distance.check,
    visibilityCheck(readings, thresholds),

    // GDS §12 lists cooperative frame width between body visibility and performance; a sitting with
    // one hero in it never reaches this row at all.
    ...(cooperative ? [cooperativeWidthCheck(readings)] : []),

    readings.frameTimeMs === null
      ? check('performance', 'pending', PENDING)
      : readings.frameTimeMs > 0 && readings.frameTimeMs <= lowPerformanceFrameMs
        ? check('performance', 'ready', null)
        : check('performance', 'attention', INVENTED_READINESS_COPY.workingHard),
  ];

  const blocked = checks.find((entry) => entry.status === 'blocked') ?? null;
  const attention = checks.find((entry) => entry.status === 'attention') ?? null;
  const ready = checks.every((entry) => entry.status === 'ready');

  const headline =
    blocked?.message ??
    attention?.message ??
    (ready && distance.mode === 'compact'
      ? READINESS_MESSAGES.compactReady
      : ready
        ? READINESS_MESSAGES.fullMotionReady
        : PENDING);

  /**
   * What this room suits, for the sitting a grown-up actually set up.
   *
   * A slow machine still gets a recommendation: CAM-06 degrades the mode, it does not refuse it. A
   * cooperative sitting has one more condition, and it is D-076(4)'s: two heroes need Full-Motion,
   * so a room whose width has not been confirmed for two recommends nothing at all rather than
   * recommending a play space it cannot fit. The alternative left on the table by that ruling — both
   * heroes at the keyboard — is what the screen says instead.
   */
  const widthConfirmed =
    !cooperative ||
    checks.some((entry) => entry.id === 'cooperativeWidth' && entry.status === 'ready');

  return {
    checks,
    headline,
    recommendedMode: blocked === null && widthConfirmed ? distance.mode : null,
    ready,
    blocked: blocked !== null,
  };
}
